import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Alignment } from '@app/core/enums/questionnaire.enums';
import { Question, Questionnaire } from '@app/core/models/questionnaire.model';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-check-box',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './check-box.html',
  styleUrl: './check-box.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckBox),
      multi: true,
    },
  ],
})
export class CheckBox implements ControlValueAccessor, OnInit {
  @Input() questionnaire: Questionnaire | null = null;
  @Input() question: Question | null = null;
  @Input() questionNumber = 0;
  @Input() isInvalid = false;
  @Input() errorMessage = '';
  @Input() formControl?: FormControl;

  @Output() answerChange = new EventEmitter<{
    questionId: string;
    options: { optionId: string; optionSelected: string }[];
  }>();

  alignment = Alignment;
  selectedValues: string[] = [];
  othersTextMap: Map<string, string> = new Map(); // Store text for each Others option
  disabled = false;
  private isUpdating = false; // Flag to prevent writeValue from overwriting during toggle
  private othersOptionIds: Set<string> = new Set(); // Cache all Others option IDs

  onChange = (value: string[]) => {};
  onTouched = () => {};

  ngOnInit() {
    // Cache all Others option IDs for faster lookups
    this.othersOptionIds = this.findOthersOptionIds();
    
    // Only set initial values once, don't subscribe continuously.
    if (this.formControl?.value && Array.isArray(this.formControl.value)) {
      this.selectedValues = [...this.formControl.value];
    }
  }

  writeValue(value: string[]): void {
    // Don't overwrite if we're currently updating internally
    if (this.isUpdating) {
      return;
    }
    
    const previousValues = new Set(this.selectedValues);
    
    if (Array.isArray(value)) {
      this.selectedValues = [...value];
    } else {
      this.selectedValues = [];
    }

    // Clear others text for options that are no longer selected
    const currentValues = new Set(this.selectedValues);
    previousValues.forEach(optionId => {
      if (!currentValues.has(optionId) && this.othersOptionIds.has(optionId)) {
        this.othersTextMap.delete(optionId);
      }
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getAlignmentClass(question: any): string {
    return question.questionSpecificOptions?.alignment === this.alignment.HORIZONTAL
      ? 'horizontal-choices'
      : 'vertical-choices';
  }

  isOthersOption(option: any): boolean {
    return option?.text?.toLowerCase().trim() === 'others';
  }

  isOthersSelected(optionId: string): boolean {
    return this.othersOptionIds.has(optionId) && this.selectedValues.includes(optionId);
  }

  private findOthersOptionIds(): Set<string> {
    const ids = new Set<string>();
    if (!this.question?.questionOptions) return ids;
    this.question.questionOptions.forEach(opt => {
      if (this.isOthersOption(opt)) {
        ids.add(opt.optionId);
      }
    });
    return ids;
  }

  toggleOption(optionId: string): void {
    if (this.disabled) return;

    // Set flag to prevent writeValue from overwriting during update
    this.isUpdating = true;

    // Toggle value - only selected options are in selectedValues array
    const index = this.selectedValues.indexOf(optionId);
    if (index > -1) {
      // Uncheck: remove from selected values
      this.selectedValues = this.selectedValues.filter(id => id !== optionId);
      // Clear others text if it was an Others option
      if (this.othersOptionIds.has(optionId)) {
        this.othersTextMap.delete(optionId);
      }
    } else {
      // Check: add to selected values (accumulate all selected checkboxes)
      this.selectedValues = [...this.selectedValues, optionId];
    }

    // Always clone to prevent mutation issues - contains ALL currently selected option IDs
    const updatedValues = [...this.selectedValues];

    // ✅ Update form control without re-triggering valueChanges
    if (this.formControl) {
      this.formControl.setValue(updatedValues, { emitEvent: false });
    }

    // ✅ Notify Angular forms - this might trigger writeValue, but our flag will prevent overwrite
    this.onChange(updatedValues);
    this.onTouched();

    // ✅ Emit payload with ALL currently selected options
    this.emitAnswer();

    // Reset flag after a short delay to allow any async operations to complete
    setTimeout(() => {
      this.isUpdating = false;
    }, 0);
  }

  onOthersTextChange(optionId: string, text: string) {
    this.othersTextMap.set(optionId, text);
    // Only emit answer, don't update form control to prevent re-render issues
  }

  onOthersInputBlur() {
    // Ensure answer is emitted when user leaves the field
    this.emitAnswer();
    this.onTouched();
  }

  getOthersText(optionId: string): string {
    return this.othersTextMap.get(optionId) || '';
  }

  private emitAnswer() {
    if (!this.question?.questionId) return;

    // Map ALL selected option IDs to the required format with optionSelected
    const formattedOptions = this.selectedValues.map(optionId => ({
      optionId,
      optionSelected: this.othersOptionIds.has(optionId) ? (this.othersTextMap.get(optionId) || '') : ''
    }));

    this.answerChange.emit({
      questionId: this.question.questionId,
      options: formattedOptions,
    });
    console.log('emitAnswer', {
      questionId: this.question.questionId,
      options: formattedOptions,
    });
  }

  isSelected(optionId: string): boolean {
    return this.selectedValues.includes(optionId);
  }
}
